import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { BlockInstanceType } from 'application-types';
import { refreshSignedExternalUrlMutation } from 'graphql-queries';
import { processBlockContentWithUrlRefresh } from './processBlockContentWithUrlRefresh';

interface UseSignedUrlRefreshOptions {
  /**
   * Buffer time before expiration to refresh URLs (in milliseconds)
   * Default: 2 minutes (120000ms)
   */
  refreshBuffer?: number;
  /**
   * Callback function to notify when a signed URL is refreshed.
   * Receives mediaId and the new URL.
   */
  onUrlRefreshed?: (mediaId: string, newUrl: string) => void;
}

interface SignedUrlInfo {
  mediaId: string;
  url: string;
  expiresAt: number;
  refreshTimeout?: NodeJS.Timeout;
}

const TWO_MINUTES_IN_MS = 2 * 60 * 1000;
const FIFTEEN_MINUTES_IN_MS = 15 * 60 * 1000;

/**
 * Hook to refresh expired signed URLs on-demand
 *
 * This hook provides functions to check and refresh signed URLs when needed,
 * with background refresh for URLs that expire while the page is open.
 *
 * Supports both:
 * - Content cards with external URLs
 * - Block content with linked rows containing PDF attachments
 */
export function useSignedUrlRefresh(options: UseSignedUrlRefreshOptions = {}) {
  const { refreshBuffer = TWO_MINUTES_IN_MS } = options;

  const [refreshUrl] = useMutation(refreshSignedExternalUrlMutation);
  const signedUrlRefs = useRef<Map<string, SignedUrlInfo>>(new Map());
  const onUrlRefreshedRef = useRef(options.onUrlRefreshed);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onUrlRefreshedRef.current = options.onUrlRefreshed;
  }, [options.onUrlRefreshed]);

  const parseUrlExpiration = useMemo(
    () =>
      (url: string): number => {
        try {
          const urlParams = new URLSearchParams(url.split('?')[1] || '');

          // CloudFront signed URL format: absolute Unix timestamp
          const cloudfrontExpires = urlParams.get('Expires');

          if (cloudfrontExpires) {
            const expiresAt = parseInt(cloudfrontExpires, 10) * 1000;

            return expiresAt;
          }

          // S3 presigned URL format: X-Amz-Date + X-Amz-Expires (relative seconds)
          const amzDate = urlParams.get('X-Amz-Date');
          const amzExpires = urlParams.get('X-Amz-Expires');

          if (amzDate && amzExpires) {
            // Parse X-Amz-Date format: YYYYMMDDTHHMMSSZ (e.g., 20251212T165314Z)
            const year = amzDate.slice(0, 4);
            const month = amzDate.slice(4, 6);
            const day = amzDate.slice(6, 8);
            const hour = amzDate.slice(9, 11);
            const minute = amzDate.slice(11, 13);
            const second = amzDate.slice(13, 15);
            const startTime = new Date(
              `${year}-${month}-${day}T${hour}:${minute}:${second}Z`
            ).getTime();
            const expiresAt = startTime + parseInt(amzExpires, 10) * 1000;

            return expiresAt;
          }
        } catch (error) {
          console.warn('Failed to parse URL expiration:', error);
        }

        // If we can't parse, assume it expires in 15 minutes
        console.warn(
          '[SignedUrlRefresh] Unknown URL format, assuming 15 min expiry'
        );

        return Date.now() + FIFTEEN_MINUTES_IN_MS;
      },
    []
  );

  // Schedule a background refresh for a URL
  const scheduleBackgroundRefresh = useCallback(
    (mediaId: string, url: string) => {
      const expiresAt = parseUrlExpiration(url);
      const now = Date.now();

      const timeUntilRefresh = expiresAt - now - refreshBuffer;

      // Clear any existing timeout
      const existing = signedUrlRefs.current.get(mediaId);

      if (existing?.refreshTimeout) {
        clearTimeout(existing.refreshTimeout);
      }

      // Only schedule refresh if it's needed
      if (timeUntilRefresh > 0) {
        const timeout = setTimeout(async () => {
          try {
            const { data } = await refreshUrl({
              variables: { mediaId },
            });

            if (data?.refreshSignedExternalUrl) {
              let urlInfo = signedUrlRefs.current.get(mediaId);

              if (!urlInfo) {
                urlInfo = {
                  mediaId,
                  url: data.refreshSignedExternalUrl,
                  expiresAt: parseUrlExpiration(data.refreshSignedExternalUrl),
                };
                signedUrlRefs.current.set(mediaId, urlInfo);
              } else {
                urlInfo.url = data.refreshSignedExternalUrl;
                urlInfo.expiresAt = parseUrlExpiration(
                  data.refreshSignedExternalUrl
                );
              }

              // Schedule the next refresh
              scheduleBackgroundRefresh(mediaId, data.refreshSignedExternalUrl);

              if (onUrlRefreshedRef.current) {
                onUrlRefreshedRef.current(
                  mediaId,
                  data.refreshSignedExternalUrl
                );
              }
            }
          } catch (error) {
            console.error(
              'Background refresh failed for mediaId:',
              mediaId,
              error
            );
          }
        }, timeUntilRefresh);

        // Store the URL info with the timeout
        signedUrlRefs.current.set(mediaId, {
          mediaId,
          url,
          expiresAt,
          refreshTimeout: timeout,
        });
      }
    },
    [refreshUrl, parseUrlExpiration, refreshBuffer]
  );

  // Check if URL needs refresh and refresh if needed
  const refreshIfNeeded = useCallback(
    async (mediaId: string, currentUrl: string): Promise<string> => {
      const now = Date.now();
      const storedUrlInfo = signedUrlRefs.current.get(mediaId);

      if (storedUrlInfo) {
        const storedTimeUntilExpiry = storedUrlInfo.expiresAt - now;

        // If we have a stored URL that's still valid, use it
        if (storedTimeUntilExpiry > refreshBuffer) {
          return storedUrlInfo.url;
        }
      }

      const expiresAt = parseUrlExpiration(currentUrl);
      const timeUntilExpiry = expiresAt - now;

      // If URL expires within the buffer time, refresh it immediately
      if (timeUntilExpiry <= refreshBuffer) {
        // Clear any pending background refresh since we're refreshing now
        if (storedUrlInfo?.refreshTimeout) {
          clearTimeout(storedUrlInfo.refreshTimeout);
        }

        try {
          const { data } = await refreshUrl({
            variables: { mediaId },
          });

          if (data?.refreshSignedExternalUrl) {
            scheduleBackgroundRefresh(mediaId, data.refreshSignedExternalUrl);

            return data.refreshSignedExternalUrl;
          }
        } catch (error) {
          console.error('Failed to refresh signed URL:', error);
        }
      } else {
        scheduleBackgroundRefresh(mediaId, currentUrl);
      }

      return currentUrl;
    },
    [refreshUrl, parseUrlExpiration, refreshBuffer, scheduleBackgroundRefresh]
  );

  // Process block content and refresh URLs for PDF attachments
  // This processes the block itself and all nested children recursively
  const processBlockContent = useCallback(
    async (
      block: BlockInstanceType | null | undefined
    ): Promise<BlockInstanceType | null | undefined> => {
      return processBlockContentWithUrlRefresh(block, refreshIfNeeded);
    },
    [refreshIfNeeded]
  );

  // Clean up timeouts when component unmounts
  const cleanup = useCallback(() => {
    signedUrlRefs.current.forEach(urlInfo => {
      if (urlInfo.refreshTimeout) {
        clearTimeout(urlInfo.refreshTimeout);
      }
    });
    signedUrlRefs.current.clear();
  }, []);

  return { refreshIfNeeded, processBlockContent, cleanup };
}
